/* global AppName */
// Required react
import React, { useCallback } from 'react'
import ReactDOM from 'react-dom'
import axios from 'axios'

// Redux
import { Provider, useSelector, useDispatch } from 'react-redux'
import { actionSetAppData, actionToggleToastActive } from './store/actions'
import store from './store/store'

// Shopify polaris
import '@shopify/polaris/styles.css'
import enTranslations from '@shopify/polaris/locales/en.json'
import { AppProvider, Page, Frame, Toast } from '@shopify/polaris'

// Routers
import { BrowserRouter, Link as ReactRouterLink, Route } from 'react-router-dom'

// Utils
import { getAppData } from './libs/utils'

// Pages
import PinPresetComponent from './pages/pin-preset/pin-preset'

import Mappers from './pages/mappers/mappers'
import NewMapperComponent from './pages/mappers/new-mapper'
import EditMapperComponent from './pages/mappers/edit-mapper'
import MapperCodeComponent from './pages/mappers/mapper-code'

import Sliders from './pages/sliders/sliders'
import NewSliderComponent from './pages/sliders/new-slider'
import EditSliderComponent from './pages/sliders/edit-slider'
import SliderCodeComponent from './pages/sliders/slider-code'

import Galleries from './pages/galleries/galleries'
import NewGalleryComponent from './pages/galleries/new-gallery'
import EditGalleryComponent from './pages/galleries/edit-gallery'
import GalleryCodeComponent from './pages/galleries/gallery-code'

// Main app styles
import '../stylesheets/app.css'

const IS_EXTERNAL_LINK_REGEX = /^(?:[a-z][a-z\d+.-]*:|\/\/)/;

const Link = ({children, url = '', external, ref, ...rest}) => {
  // react-router only supports links to pages it can handle itself. It does not
  // support arbirary links, so anything that is not a path-based link should
  // use a reglar old `a` tag
  if (external || IS_EXTERNAL_LINK_REGEX.test(url)) {
    rest.target = '_blank'
    rest.rel = 'noopener noreferrer'
    return (
      <a href={url} {...rest}>
        {children}
      </a>
    )
  }

  return (
    <ReactRouterLink to={url} {...rest}>
      {children}
    </ReactRouterLink>
  )
}

const Wrapper = (props) => {
  const dispatch = useDispatch()
  dispatch(actionSetAppData(props.data))
  const data = useSelector(state => state.app)
  const toastActive = useSelector(state => state.toastActive)
  const toastMessage = useSelector(state => state.toastMessage)
  const toastError = useSelector(state => state.toastError)

  const toggleToastActive = useCallback(() => {
    dispatch(actionToggleToastActive(false))
  }, [])
  const toastMarkup = (toastActive && toastMessage) ? (
    <Toast error={toastError} content={toastMessage} onDismiss={toggleToastActive} />
  ) : null
  return (
    <AppProvider
      i18n={enTranslations}
      linkComponent={Link}
    >
      <Frame>
        <Page
          fullWidth
          title={data.appName}
          secondaryActions={[
            {
              content: 'Mapper',
              url: '/'
            },
            {
              content: 'Slider',
              url: '/sliders'
            },
            {
              content: 'Gallery',
              url: '/galleries'
            },
            {
              content: 'Pin Preset',
              url: '/presets'
            },
            {
              content: 'Documentation',
              external: true,
              url: '/docs#/'
            },
            {
              content: 'FAQ',
              external: true,
              url: '/docs#/faqs'
            },
            {
              content: 'Support',
              external: true,
              url: 'mailto:lucdkny@gmail.com'
            }
          ]}
        />
        {props.children}
        {toastMarkup}
      </Frame>
    </AppProvider>
  )
}

const App = (props) => {
  return (
    <Provider store={store}>
      <BrowserRouter>
        <Wrapper {...props} />
      </BrowserRouter>
    </Provider>
  )
}

document.addEventListener('DOMContentLoaded', () => {
  const mainAppEl = document.querySelector('#main-app')
  const data = getAppData()

  axios.interceptors.request.use(function (config) {
    return shopify.idToken()
      .then((token) => {
        config.headers.Authorization = `Bearer ${token}`
        return config
      })
  })

  ReactDOM.render(
    <App data={data}>
      {/*Homepage*/}
      <Route exact path='/' component={Mappers} />

      {/*Mappers related routers*/}
      <Route path='/mappers/new' component={NewMapperComponent} />
      <Route path='/mappers/:id/edit' component={EditMapperComponent} />
      <Route path='/mappers/:id/code' component={MapperCodeComponent} />

      {/*Sliders related routers*/}
      <Route exact path='/sliders' component={Sliders} />
      <Route path='/sliders/new' component={NewSliderComponent} />
      <Route path='/sliders/:id/edit' component={EditSliderComponent} />
      <Route path='/sliders/:id/code' component={SliderCodeComponent} />

      {/*galleries related routers*/}
      <Route exact path='/galleries' component={Galleries} />
      <Route path='/galleries/new' component={NewGalleryComponent} />
      <Route path='/galleries/:id/edit' component={EditGalleryComponent} />
      <Route path='/galleries/:id/code' component={GalleryCodeComponent} />

      {/*Preset related routers*/}
      <Route exact path='/presets' component={PinPresetComponent} />
    </App>,
    mainAppEl
  )
})
